import React, { useState, useRef } from "react";
import { DropdownComponent, InputComponent } from "../../core/Forms";
import { ServiceData, ServicePriceData } from "../../../@types/service";
import { commasToNumber } from "../../../utils/convertToFloat";
import { Button } from "../../common";
import { Spinner } from "flowbite-react";
import DateTimePicker from "react-datetime-picker";
import "react-datetime-picker/dist/DateTimePicker.css";
import "react-calendar/dist/Calendar.css";
import "react-clock/dist/Clock.css";
import { calculateDurationFromDate } from "../../../utils/duration";
import { minDate } from "../../../utils/duration";
import { clearFieldError } from "../../../utils/handleErrors";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "../../../state/useCompanyStore";
import { useGetPaymentCurrencyList } from "../../../utils";
import ServiceType from "./ServiceType";
import { ServiceTypes } from "../../../@types/invoice";

type ErrorProps = {
  name: string;
  charge: Record<string, string | undefined>;
};

interface ServiceFormProps {
  serviceData: ServiceData;
  setServiceData: (ServiceData: ServiceData) => void;
  handleCreateService: (e: React.FormEvent<HTMLFormElement>) => void;
  setPrice: (ServicePrice: ServicePriceData[]) => void;
  price: ServicePriceData[];
  error: ErrorProps;
  setError: any;
  isLoading: boolean;
}
type ValuePiece = Date | null;
type Value = ValuePiece | [ValuePiece, ValuePiece];

function ServiceForm({
  serviceData,
  setServiceData,
  handleCreateService,
  setPrice,
  price,
  error,
  setError,
  isLoading,
}: ServiceFormProps) {
  // Create refs for the form fields
  const nameRef = useRef<HTMLInputElement>(null);
  const chargeNGNRef = useRef<HTMLInputElement>(null);
  const chargeUSDRef = useRef<HTMLInputElement>(null);
  const chargeGBPRef = useRef<HTMLInputElement>(null);
  const [serviceType, setServiceType] = useState<ServiceTypes>(
    ServiceTypes.time_based,
  );

  const companyProfile = useCompanyStore();
  const { userCurrencyCode } = useGetPaymentCurrencyList(
    companyProfile.company,
  );
  const userLocalCurrency = userCurrencyCode.currency;
  const userCountry = companyProfile.company.country === "US" ? "CAD" : "USD";

  // Function to scroll to the first error
  const scrollToError = () => {
    if (error.name && nameRef.current) {
      nameRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    } else if (error.charge[userLocalCurrency] && chargeNGNRef.current) {
      chargeNGNRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (error.charge[userCountry] && chargeUSDRef.current) {
      chargeUSDRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    } else if (error.charge.GBP && chargeGBPRef.current) {
      chargeGBPRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };
  const { t } = useTranslation("services");
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;

    const updatedServiceData = { ...serviceData, [name]: value };
    setServiceData(updatedServiceData);
    clearFieldError(name, error, setError);
  };
  const handleServiceTypeChange = (type: string) => {
    const updatedServiceData = { ...serviceData, serviceType: type };
    setServiceData(updatedServiceData);
  };

  const handleServicePrice = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const formattedValue = commasToNumber(value);
    const existingPriceIndex = price.findIndex(
      (p: ServicePriceData) => p.currency === name,
    );

    if (formattedValue === "") {
      if (existingPriceIndex !== -1) {
        const updatedPrice = [...price];
        updatedPrice.splice(existingPriceIndex, 1);
        setPrice(updatedPrice);
        setError({ ...error, charge: { [name]: "" } });
      }
    } else {
      const updatedPrice = [...price];
      if (existingPriceIndex === -1) {
        updatedPrice.push({ amount: formattedValue, currency: name });
      } else {
        updatedPrice[existingPriceIndex].amount = formattedValue;
      }
      setPrice(updatedPrice);
      setError({ ...error, charge: { [name]: "" } });
    }
  };

  const [duration, setDuration] = useState<Value>(new Date());

  const handleDuration = (value: Value) => {
    if (value === null) {
      const today = new Date();
      setDuration(today);
      const newDuration = calculateDurationFromDate(today);
      const updatedServiceData = { ...serviceData, duration: newDuration };
      setServiceData(updatedServiceData);
    } else {
      setDuration(value);

      const newDuration = calculateDurationFromDate(value);
      const updatedServiceData = { ...serviceData, duration: newDuration };
      setServiceData(updatedServiceData);
    }
  };

  React.useEffect(() => {
    scrollToError();
  }, [error]);

  const DURATION = [
    { value: "Minutes", label: "Minutes" },
    { value: "Hours", label: "Hours" },
  ];

  const handleDropDownChange = (name: string, selectedValue: string) => {
    setServiceData({ ...serviceData, [name]: selectedValue });
    clearFieldError(name, error, setError);
  };

  return (
    <div className="mt-3 lg:h-[300px]">
      <form onSubmit={handleCreateService}>
        <ServiceType
          serviceType={serviceType}
          setServiceType={setServiceType}
          handleServiceTypeChange={handleServiceTypeChange}
        />
        <div className="space-y-6 mt-5">
          <div className="flex flex-col gap-6 lg:flex-row lg:gap-8">
            <div className="w-full space-y-2 md:space-y-3 lg:space-y-2">
              <label
                htmlFor="Service Name"
                className="text-[14px] lg:text-[16px] text-gray-800 font-medium leading-6 flex items-center"
              >
                {t("service.serviceName")}
                <p className="leading-5 text-[#F04438] mt-2 pl-1">*</p>
              </label>
              <InputComponent
                ref={nameRef} // Attach the ref
                className="w-full py-3 px-4 border border-[#D0D5DD] rounded-[4px] block"
                type="text"
                name="name"
                placeholder="Enter Name"
                value={serviceData.name}
                handleChange={handleInputChange}
                error={error.name}
              />
            </div>
          </div>
          <div className="lg:flex lg:gap-8 lg:items-center">
            <div className="w-full space-y-2 md:space-y-3 lg:space-y-2">
              <div className="flex justify-between lg:text-[16px] ">
                <label
                  className="text-[14px] lg:text-[16px] text-gray-800 font-medium leading-6 flex items-center"
                  htmlFor=""
                >
                  {t("service.serviceCharge")}{" "}
                  {serviceType === "time-based" && "(Per Hour)"}
                  <p className="leading-5 text-[#F04438] mt-2 pl-1">*</p>
                </label>
                <p className="font-semibold">{userLocalCurrency}</p>
              </div>
              <InputComponent
                ref={chargeNGNRef} // Attach the ref
                className="w-full py-3 px-4 border border-[#D0D5DD] rounded-[4px] block"
                type="text"
                name={userLocalCurrency}
                placeholder="0.00"
                value={
                  price.find((p: any) => p.currency === userLocalCurrency)
                    ?.amount || ""
                }
                handleChange={handleServicePrice}
                error={error.charge[userLocalCurrency]}
              />
            </div>
          </div>
          <div className="w-full space-y-2 md:space-y-3">
            <label
              htmlFor="Description"
              className="text-[14px] lg:text-[16px] text-gray-800 font-medium leading-6"
            >
              {t("service.description")}
            </label>
            <textarea
              className="w-full h-[80px] md:h-[140px] py-3 px-4 border border-[#D0D5DD] rounded-[4px] block"
              name="description"
              placeholder="Describe Service..."
              value={serviceData.description}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className=" mt-6 text-[14px]">
          <div className="border border-[#D0D5DD]/40 w-full mb-[22px]" />
          <h2 className="font-semibold text-[16px] my-3">Optional</h2>
          <div className="lg:flex gap-8">
            <div className="mb-6 w-full">
              <div className="flex justify-between lg:text-[16px] mb-1">
                <label
                  htmlFor=""
                  className="text-[14px] lg:text-[16px] text-gray-800 font-medium leading-6"
                >
                  {" "}
                  {t("service.serviceCharge")}
                </label>
                <p className="font-semibold">{userCountry}</p>
              </div>
              <InputComponent
                ref={chargeUSDRef} // Attach the ref
                className="w-full py-3 px-4 border border-[#D0D5DD] rounded-[4px] block"
                type="text"
                name={userCountry}
                placeholder={`${userCountry} 0.00`}
                value={
                  price.find((p: any) => p.currency === userCountry)?.amount ||
                  ""
                }
                handleChange={handleServicePrice}
                error={error.charge[userCountry]}
              />
            </div>
            <div className="mb-6 w-full">
              <div className="flex justify-between lg:text-[16px] mb-1">
                <label
                  htmlFor=""
                  className="text-[14px] lg:text-[16px] text-gray-800 font-medium leading-6"
                >
                  {" "}
                  {t("service.serviceCharge")}
                </label>
                <p className="font-semibold">GBP</p>
              </div>
              <InputComponent
                ref={chargeGBPRef} // Attach the ref
                className="w-full py-3 px-4 border border-[#D0D5DD] rounded-[4px] block"
                type="text"
                name="GBP"
                placeholder="GBP 0.00"
                value={
                  price.find((p: any) => p.currency === "GBP")?.amount || ""
                }
                handleChange={handleServicePrice}
                error={error.charge.GBP}
              />
            </div>
          </div>
        </div>

        <div className="md:flex justify-end md:gap-4 mt-[-1em] md:mt-16 lg:mt-[0.5em]">
          <div className=" md:w-[215px] md:relative">
            <button
              type="submit"
              className="w-full h-[48px] md:h-[52px] text-[16px] bg-[#044E97] border font-semibold rounded-[4px]  text-white inline-flex items-center justify-center whitespace-nowrap"
            >
              {isLoading ? (
                <div className="mr-3">
                  <Spinner size="sm" light={true} />
                </div>
              ) : (
                t("service.createService")
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ServiceForm;
